import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Download, BookText, TrendingUp, Package, AlertTriangle, CreditCard, ArrowRight } from "lucide-react";
import { api, exportUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num } from "@/lib/format";
import { Card, Loading, PageHeader, StatCard } from "@/components/common";
import DateRangeFilter, { rangeForPreset } from "@/components/DateRangeFilter";

const EXPORTS = [["sales", "Penjualan"], ["pnl", "Laba Rugi"], ["cashbook", "Buku Kas"], ["stock", "Stok"], ["spoilage", "Waste"]];

export default function Reports() {
  const { branch } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [daily, setDaily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dr, setDr] = useState(() => rangeForPreset("1m"));

  const branchId = branch?.id;
  useEffect(() => { api.get("/reports/daily", branchId ? { branch_id: branchId } : {}).then(setDaily).catch(() => setDaily(null)); }, [branchId]);
  useEffect(() => {
    let alive = true; setLoading(true);
    (async () => {
      try {
        const params = { range: "custom", date_from: dr.from, date_to: dr.to };
        if (branchId) params.branch_id = branchId;
        const d = await api.get("/owner/pnl", params);
        if (alive) setData(d);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [branchId, dr]);

  const c = data?.consolidated;
  const expParams = { date_from: dr.from, date_to: dr.to, ...(branchId ? { branch_id: branchId } : {}) };

  const CARDS = daily ? [
    { label: "Kas Harian", value: rupiah(daily.kas?.net), sub: `Masuk ${rupiah((daily.kas?.sales_cash || 0) + (daily.kas?.manual_in || 0))}`, icon: BookText, tone: "text-primary", to: "/cashbook" },
    { label: "Omset Penjualan", value: rupiah(daily.omset?.total), sub: `${num(daily.omset?.orders || 0)} order`, icon: TrendingUp, tone: "text-emerald-600", to: "/sales-report" },
    { label: "Rekap Stok", value: rupiah(daily.stok?.value), sub: `${num(daily.stok?.low_stock || 0)} stok menipis`, icon: Package, tone: "text-sky-600", to: "/stock-report" },
    { label: "Spoilage", value: rupiah(daily.spoilage?.today_value), sub: `${num(daily.spoilage?.today_qty || 0)} unit hari ini`, icon: AlertTriangle, tone: "text-amber-600", to: "/waste" },
    { label: "Hutang Supplier", value: rupiah(daily.hutang_supplier?.outstanding), sub: `${num(daily.hutang_supplier?.count || 0)} tagihan`, icon: CreditCard, tone: "text-rose-600", to: "/supplier-debts" },
  ] : [];

  return (
    <>
      <PageHeader title="Laporan Laba Rugi" subtitle={branch?.name || "Semua Unit"} icon={BarChart3} />

      <div className="mb-5"><DateRangeFilter value={dr} onChange={setDr} testid="report-range-filter" /></div>

      {/* 5 kartu ringkasan clickable */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {CARDS.map((k) => (
          <button key={k.label} onClick={() => nav(k.to)} data-testid={`sumcard-${k.to.slice(1)}`} className="text-left group">
            <Card className="p-4 group-hover:border-primary group-hover:shadow-md transition h-full">
              <div className="flex items-center justify-between"><span className="text-xs font-medium text-slate-500">{k.label}</span><k.icon size={16} className="text-slate-400 group-hover:text-primary" /></div>
              <div className={`text-xl font-bold mt-1.5 ${k.tone}`}>{k.value}</div>
              <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">{k.sub} <ArrowRight size={11} className="opacity-0 group-hover:opacity-100 transition" /></div>
            </Card>
          </button>
        ))}
      </div>

      {loading ? <Loading /> : !c ? null : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Penjualan Netto" value={rupiah(c.net_sales)} sub={`${num(c.orders)} order`} tone="primary" testid="rep-sales" />
            <StatCard label="HPP + Waste" value={rupiah(c.cogs + c.waste)} sub={`HPP ${rupiah(c.cogs)}`} />
            <StatCard label="Biaya Operasional" value={rupiah(c.operating_expenses)} tone="warning" />
            <StatCard label="Laba Bersih" value={rupiah(c.net_profit)} tone={c.net_profit >= 0 ? "success" : "danger"} testid="rep-profit" />
          </div>

          <Card className="p-5 mb-6">
            <h3 className="font-semibold text-slate-800 mb-4">Rincian Laba Rugi</h3>
            <div className="space-y-1.5 text-sm max-w-xl">
              {[
                ["Penjualan Bruto", c.gross_sales], ["Diskon", -c.discount], ["Penjualan Netto", c.net_sales, true],
                ["HPP (Bahan Terjual)", -c.cogs], ["Waste / Bunga Rusak", -c.waste], ["Laba Kotor", c.gross_profit, true],
                ["Biaya Operasional", -c.operating_expenses], ["Laba Bersih", c.net_profit, true],
              ].map(([label, val, bold], i) => (
                <div key={i} className={`flex justify-between py-1 ${bold ? "border-t border-slate-100 font-semibold text-slate-800" : "text-slate-500"}`}>
                  <span>{label}</span>
                  <span className={val < 0 ? "text-rose-600" : ""}>{rupiah(val)}</span>
                </div>
              ))}
            </div>
          </Card>

          {(data.units || []).length > 0 && (
            <Card className="p-5 mb-6">
              <h3 className="font-semibold text-slate-800 mb-4">Per Unit Bisnis</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-slate-100">
                      <th className="py-2 font-medium">Unit</th><th className="py-2 font-medium text-right">Omset</th><th className="py-2 font-medium text-right">HPP</th><th className="py-2 font-medium text-right">Waste</th><th className="py-2 font-medium text-right">Biaya</th><th className="py-2 font-medium text-right">Laba</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.units.map((u) => (
                      <tr key={u.unit_id} className="border-b border-slate-50">
                        <td className="py-2.5 font-medium text-slate-700">{u.name}</td>
                        <td className="py-2.5 text-right">{rupiah(u.net_sales)}</td>
                        <td className="py-2.5 text-right text-slate-500">{rupiah(u.cogs)}</td>
                        <td className="py-2.5 text-right text-slate-500">{rupiah(u.waste)}</td>
                        <td className="py-2.5 text-right text-slate-500">{rupiah(u.operating_expenses)}</td>
                        <td className={`py-2.5 text-right font-semibold ${u.net_profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{rupiah(u.net_profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <Card className="p-5">
            <h3 className="font-semibold text-slate-800 mb-3">Ekspor Excel</h3>
            <div className="flex flex-wrap gap-2">
              {EXPORTS.map(([kind, label]) => (
                <a key={kind} href={exportUrl(kind, expParams)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 h-9 rounded-xl border border-slate-300 text-sm text-slate-700 hover:bg-slate-50" data-testid={`export-${kind}`}>
                  <Download size={15} /> {label}
                </a>
              ))}
            </div>
          </Card>
        </>
      )}
    </>
  );
}
